import Api from "entities/helpers/api";

export const fetchContactInfo = async (ids) => {
  const contactMap = {};
  const gql = String.raw;
  const query = gql`
    query ($ids: [BigInt!]) {
      contacts(ids: $ids) {
        contact_attributes {
          name_full
        }
        profile_image {
          url
        }
        id
      }
    }
  `;
  // use the contact ids to retrieve the full name from graph ql without having to load the whole contact obj
  const contactInfo = await Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      operationName: null,
      query: String.raw`${query}`,
      variables: { ids: ids },
    }),
  });

  contactInfo.data.contacts.forEach((contact) => {
    if (contact) {
      const { contact_attributes, id, profile_image } = contact;
      contactMap[id] = { contact_attributes, profile_image };
    }
  });
  return contactMap;
};

const fetchCreatorInfo = async (ids) => {
  const userMap = {};
  const gql = String.raw;
  const query = gql`
    query ($ids: [Int!]) {
      users(ids: $ids) {
        name
        id
      }
    }
  `;

  const creatorInfo = await Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      operationName: null,
      query: String.raw`${query}`,
      variables: { ids: ids },
    }),
  });

  creatorInfo.data.users.forEach((creator) => {
    if (creator) {
      const { id, name } = creator;
      userMap[id] = { name };
    }
  });
  return userMap;
};

const mapSourceDataToTarget = (target, source, targetField, newField) => {
  target.forEach((target) => {
    if (target[targetField]) {
      target[newField] = source[target[targetField]];
    }
  });
};

const removeDuplicateTasks = (tasks) => {
  const taskMap = new Set();
  return tasks.reduce((accum, task) => {
    if (!taskMap.has(task.id)) {
      taskMap.add(task.id);
      return [...accum, task];
    }
    return accum;
  }, []);
};

export const fetchTasks = async (offset, limit, id, referrer, sortReverse, userId, userType) => {
  const contactsSet = new Set();
  const usersSet = new Set();
  const ownersSet = new Set();
  const PATH = referrer === "contact" ? "CONTACT_TASKS" : "USER_TASKS";
  const URL_ARGS = referrer === "contact" ? { contact_id: id } : { user_id: id };
  try {
    const resp = await Api.JOURNEYS[PATH].get({
      urlArgs: URL_ARGS,
      params: { offset, limit, sort_order: sortReverse ? "DESC" : "ASC", user_type: userType },
    });

    resp.items.forEach(({ target_contact_id, creator_user_id, owner_user_id }) => {
      if (target_contact_id) {
        contactsSet.add(target_contact_id);
      }
      if (creator_user_id) {
        usersSet.add(creator_user_id);
      }
      if (owner_user_id) {
        ownersSet.add(owner_user_id);
      }
    });

    const contactInfo = await fetchContactInfo(Array.from(contactsSet));
    mapSourceDataToTarget(resp.items, contactInfo, "target_contact_id", "target_info");
    const creatorInfo = await fetchCreatorInfo(Array.from(usersSet));
    const ownerInfo = await fetchCreatorInfo(Array.from(ownersSet));
    mapSourceDataToTarget(resp.items, creatorInfo, "creator_user_id", "creator_info");
    mapSourceDataToTarget(resp.items, ownerInfo, "owner_user_id", "owner_info");

    return { items: removeDuplicateTasks(resp.items), total: resp.total };
  } catch (e) {
    throw e;
  }
};

export const fetchTasksByAppliedFilters = async (taskFilters) => {
  const contactsSet = new Set();
  const usersSet = new Set();
  const ownersSet = new Set();
  const gql = String.raw;
  const query = gql`
    query (
      $id: BigInt
      $status: String
      $target_contact_id: BigInt
      $creator_user_id: BigInt
      $owner_user_id: BigInt
      $exclude: [JSON]
      $offset: Int
      $limit: Int
      $sort: [String]
    ) {
      tasks(
        id: $id
        status: $status
        target_contact_id: $target_contact_id
        creator_user_id: $creator_user_id
        owner_user_id: $owner_user_id
        exclude: $exclude
        offset: $offset
        limit: $limit
        sort: $sort
      ) {
        items {
          id
          action_type
          comments
          completed_at
          contact_journey_id
          created_at
          creator_user_id
          deleted_at
          description
          due_date
          journey_task_id
          oid
          owner_user_id
          status
          target_contact_id
          title
          updated_at
          updater_user_id
        }
        total
      }
    }
  `;

  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: taskFilters,
      }),
    });
    const tasksResponse = resp.data.tasks.items;

    tasksResponse.forEach((task) => {
      if (task.target_contact_id) {
        contactsSet.add(task.target_contact_id);
      }
      if (task.creator_user_id) {
        usersSet.add(task.creator_user_id);
      }
      if (task.owner_user_id) {
        ownersSet.add(task.owner_user_id);
      }
      task.due_date = Date.parse(task.due_date) / 1000;
      task.completed_at = Date.parse(task.completed_at) / 1000;
    });
    const contactInfo = await fetchContactInfo(Array.from(contactsSet));
    mapSourceDataToTarget(tasksResponse, contactInfo, "target_contact_id", "target_info");
    const creatorInfo = await fetchCreatorInfo(Array.from(usersSet));
    const ownerInfo = await fetchCreatorInfo(Array.from(ownersSet));
    mapSourceDataToTarget(tasksResponse, creatorInfo, "creator_user_id", "creator_info");
    mapSourceDataToTarget(tasksResponse, ownerInfo, "owner_user_id", "owner_info");

    return { items: removeDuplicateTasks(tasksResponse), total: resp.data.tasks.total };
  } catch (e) {
    throw e;
  }
};

export const fetchAllTasksCount = async (taskFilters) => {
  const gql = String.raw;
  const query = gql`
    query ($user_id: BigInt) {
      allTasksCount(user_id: $user_id) {
        total
      }
    }
  `;

  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: taskFilters,
      }),
    });
    return { total: resp.data.allTasksCount.total };
  } catch (e) {
    throw e;
  }
};
